import React, { useState, useCallback } from 'react';
import { Button, CircularProgress, Box, Typography } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import client from '../API';

const PayPalConnectButton = ({
  onError,
  isCreator = false,
  variant = 'contained',
  fullWidth = false,
  disabled = false,
  redirectPath = null,
  size = 'medium',
  // Claim flow: pass claimToken to use token-based (unauthenticated) initiation
  claimToken = null,
}) => {
  const [loading, setLoading] = useState(false);

  const handleConnect = useCallback(async () => {
    try {
      setLoading(true);

      let response;
      if (claimToken) {
        // Unauthenticated claim flow — token-based PayPal OAuth
        response = await client.payouts.initiatePaypalOAuth(claimToken);
      } else {
        // Authenticated user/creator flow
        const api = isCreator
          ? client.creatorConnect.paypalOAuth
          : client.userWallets.paypalOAuth;

        const path = redirectPath || (isCreator ? '/creatorconnect/dashboard' : '/dashboard?tab=account');
        response = await api.initiate(path);
      }

      if (response.authorization_url) {
        // Redirect to PayPal for authentication
        window.location.href = response.authorization_url;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error) {
      setLoading(false);
      console.error('PayPal OAuth error:', error);

      // Handle specific error cases
      if (error.response?.status === 409) {
        onError?.(new Error('PayPal account already connected'));
      } else {
        onError?.(error);
      }
    }
  }, [isCreator, redirectPath, claimToken, onError]);

  return (
    <Button
      variant={variant}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={handleConnect}
      size={size}
      startIcon={
        loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <AccountBalanceWalletIcon />
        )
      }
      sx={{
        backgroundColor: '#0070ba',
        '&:hover': {
          backgroundColor: '#005ea6',
        },
        color: 'white',
        textTransform: 'none',
        fontWeight: 600,
        py: 1.5,
        px: 3,
        borderRadius: 2,
      }}
    >
      {loading ? 'Connecting...' : 'Connect with PayPal'}
    </Button>
  );
};

export const PayPalVerifiedBadge = ({ email, verifiedAt }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 2,
        bgcolor: 'success.light',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'success.main',
      }}
    >
      <CheckCircleIcon color="success" />
      <Box>
        <Typography variant="body1" fontWeight={600} color="success.dark">
          PayPal Connected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {email}
        </Typography>
      </Box>
    </Box>
  );
};

export default PayPalConnectButton;
